function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
function _objectWithoutProperties(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as Dialog from "@radix-ui/react-dialog";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import React, { createContext, forwardRef, useContext, useMemo, useRef } from "react";
import { inlineEasingGradient } from "../css/gradient";
import { Sticky } from "../css/Sticky";
import { Base } from "../palette/base";
import { semanticColor } from "../palette/semantic";
import CloseIcon from "../svg/close.svg";
import { IconButton } from "./IconButton";
import { MiniBackButton } from "./MiniBackButton";
import { MiniCloseButton } from "./MiniCloseButton";
var getContentAnimations = function(layout) {
    switch(layout){
        case "panel":
            return {
                variants: {
                    closed: {
                        opacity: 0,
                        y: "50%",
                        transform: "scale(0.94)"
                    },
                    open: {
                        opacity: 1,
                        y: 0,
                        transform: "scale(1)"
                    }
                },
                initial: "closed",
                animate: "open",
                exit: "closed",
                transition: {
                    duration: 0.3,
                    bounce: 0
                }
            };
        default:
            return {
                variants: {
                    closed: {
                        opacity: 0,
                        transform: "scale(0.94)"
                    },
                    open: {
                        opacity: 1,
                        transform: "scale(1)"
                    }
                },
                initial: "closed",
                animate: "open",
                exit: "closed",
                transition: {
                    duration: 0.3,
                    bounce: 0
                }
            };
    }
};
var getOverlayAnimations = function(_layout) {
    return {
        initial: {
            opacity: 0
        },
        animate: {
            opacity: 1
        },
        exit: {
            opacity: 0
        },
        transition: {
            duration: 0.3,
            bounce: 0
        }
    };
};
var Context = /*#__PURE__*/ createContext(null);
var useModalContext = function() {
    var context = useContext(Context);
    if (!context) {
        return {
            isOpen: true,
            layout: "dialog",
            fillX: true,
            fillY: true,
            dismissButton: false,
            autoFocusOnOpen: true
        };
    }
    return context;
};
// TODO: escape key to dismiss modal
export var Modal = function(props) {
    var className = props.className, isOpen = props.isOpen, _fillX = props.fillX, fillX = _fillX === void 0 ? false : _fillX, _fillY = props.fillY, fillY = _fillY === void 0 ? false : _fillY, layout = props.layout, children = props.children, contentClassName = props.contentClassName, _dismissButton = props.dismissButton, dismissButton = _dismissButton === void 0 ? true : _dismissButton, _autoFocusOnOpen = props.autoFocusOnOpen, autoFocusOnOpen = _autoFocusOnOpen === void 0 ? false : _autoFocusOnOpen, onDismiss = props.onDismiss, onEnter = props.onEnter, onExitComplete = props.onExitComplete, _animateOnMount = props.animateOnMount, animateOnMount = _animateOnMount === void 0 ? false : _animateOnMount;
    var contentsRef = useRef(null);
    var context = useMemo(function() {
        return {
            isOpen: isOpen,
            layout: layout,
            fillX: fillX,
            fillY: fillY,
            dismissButton: dismissButton,
            autoFocusOnOpen: autoFocusOnOpen
        };
    }, [
        isOpen,
        layout,
        fillX,
        fillY,
        dismissButton
    ]);
    return /*#__PURE__*/ _jsx(Dialog.Root, {
        modal: true,
        open: isOpen,
        onOpenChange: function(open) {
            if (!open) {
                onDismiss === null || onDismiss === void 0 ? void 0 : onDismiss();
            }
        },
        children: /*#__PURE__*/ _jsx(AnimatePresence, {
            initial: animateOnMount,
            onExitComplete: onExitComplete,
            children: isOpen && /*#__PURE__*/ _jsx(Dialog.Portal, {
                forceMount: true,
                children: /*#__PURE__*/ _jsx(Dialog.Overlay, {
                    asChild: true,
                    children: /*#__PURE__*/ _jsxs(motion.div, _objectSpread({
                        className: classNames("fixed top-0 right-0 bottom-0 left-0 isolate z-[1] flex h-full flex-col items-stretch justify-end", layout === "panel" ? "p-0 pt-[52px] sm:px-[72px] sm:pt-10" : "sm:items-center sm:justify-center sm:p-[72px]", layout === "transparent" ? "bg-[rgba(0,0,0,0.85)]" : "bg-[rgba(0,0,0,0.75)]", className),
                        onClick: function(e) {
                            if (e.target === e.currentTarget) {
                                onDismiss === null || onDismiss === void 0 ? void 0 : onDismiss();
                            }
                        },
                        onAnimationStart: function(d) {
                            if (d === "open") {
                                onEnter === null || onEnter === void 0 ? void 0 : onEnter();
                            }
                        }
                    }, getOverlayAnimations(layout), {
                        children: [
                            /*#__PURE__*/ _jsx(Dialog.Content, {
                                asChild: true,
                                onOpenAutoFocus: function(e) {
                                    if (!autoFocusOnOpen) {
                                        var ref;
                                        e.preventDefault();
                                        (ref = contentsRef.current) === null || ref === void 0 ? void 0 : ref.focus();
                                    }
                                },
                                onKeyDown: function(e) {
                                    // Focus first input or textarea on input keypress.
                                    // This will ignore presses that don't correspond to printable ASCII chars.
                                    if (e.target === e.currentTarget && e.key.length === 1 && e.key !== " " && !(e.ctrlKey || e.metaKey) && e.key.charCodeAt(0) >= 32 && e.key.charCodeAt(0) <= 126) {
                                        var ref;
                                        var input = (ref = e.currentTarget) === null || ref === void 0 ? void 0 : ref.querySelector("input, textarea");
                                        if (_instanceof(input, HTMLElement)) {
                                            input.focus();
                                        }
                                    }
                                },
                                onEscapeKeyDown: function(e) {
                                    var ref;
                                    if (_instanceof(e.target, HTMLElement) && (e.target === contentsRef.current || !((ref = contentsRef.current) === null || ref === void 0 ? void 0 : ref.contains(e.target)))) {
                                        return;
                                    }
                                    if (_instanceof(e.target, HTMLElement) && e.target.closest("input, textarea, button")) {
                                        e.preventDefault();
                                    }
                                },
                                children: /*#__PURE__*/ _jsxs(motion.div, _objectSpread({
                                    tabIndex: -1,
                                    ref: contentsRef,
                                    className: classNames("relative max-h-full w-full bg-[transparent] focus-visible:outline-none", {
                                        "h-full": fillY,
                                        "sm:w-auto": !fillX,
                                        "w-full self-center sm:max-w-6xl": layout === "panel",
                                        "w-full self-center": layout === "transparent"
                                    }, contentClassName, layout === "transparent" ? "pointer-events-none" : "")
                                }, getContentAnimations(layout), {
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: classNames("text-primary isolate flex h-full w-full flex-col items-stretch overflow-hidden [&>*]:pointer-events-auto", layout === "transparent" ? "bg-[transparent]" : "bg-tertiary-100", {
                                                "rounded-tl-3xl rounded-tr-3xl": layout !== "transparent",
                                                "sm:flex-center sm:h-auto sm:rounded-3xl": layout === "dialog"
                                            }),
                                            children: [
                                                /*#__PURE__*/ _jsx(Context.Provider, {
                                                    value: context,
                                                    children: children
                                                }),
                                                onDismiss && dismissButton && layout === "dialog" ? /*#__PURE__*/ _jsx(IconButton, {
                                                    className: "absolute top-3 right-3 !hidden sm:!flex",
                                                    variant: "secondary",
                                                    shape: "square",
                                                    btnSize: 34,
                                                    onClick: onDismiss,
                                                    "aria-label": "Dismiss",
                                                    children: /*#__PURE__*/ _jsx(CloseIcon, {})
                                                }) : null
                                            ]
                                        }),
                                        onDismiss && dismissButton ? /*#__PURE__*/ _jsx(MiniCloseButton, {
                                            className: classNames("absolute right-4 sm:!hidden", {
                                                "bottom-full -translate-y-4": layout !== "transparent",
                                                "top-0 translate-y-4": layout === "transparent"
                                            }),
                                            onClick: onDismiss
                                        }) : null
                                    ]
                                }), "content")
                            }),
                            onDismiss && dismissButton && layout !== "dialog" ? /*#__PURE__*/ _jsx(IconButton, {
                                className: "absolute top-3 right-3 !hidden sm:!flex",
                                variant: "secondary",
                                shape: "square",
                                onClick: onDismiss,
                                "aria-label": "Dismiss",
                                children: /*#__PURE__*/ _jsx(CloseIcon, {})
                            }) : null
                        ]
                    }), "overlay")
                })
            })
        })
    });
};
var ModalMain = /*#__PURE__*/ forwardRef(function(props, ref) {
    var children = props.children, className = props.className, footer = props.footer, _scroll = props.scroll, scroll = _scroll === void 0 ? true : _scroll, rest = _objectWithoutProperties(props, [
        "children",
        "className",
        "footer",
        "scroll"
    ]);
    var _$ref = useModalContext(), layout = _$ref.layout, fillX = _$ref.fillX;
    return /*#__PURE__*/ _jsxs("div", _objectSpread({}, rest, {
        ref: ref,
        className: classNames("flex-1 focus-visible:outline-none", scroll ? "overflow-auto" : "overflow-hidden", {
            "sm:w-full": fillX,
            "sm:max-w-full": fillX,
            "sm:max-w-[580px]": !fillX
        }),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: classNames("flex min-h-full w-full flex-col items-stretch py-8 px-4", {
                    "sm:py-10 sm:px-10": layout === "panel",
                    "sm:items-center sm:justify-center sm:py-12 sm:px-20": layout === "dialog",
                    "sm:max-w-full": layout === "dialog" && fillX,
                    "sm:max-w-[580px]": layout === "dialog" && !fillX
                }, className),
                children: children
            }),
            footer
        ]
    }));
});
export { ModalMain };
export var ModalBackButtonMobile = function(props) {
    return /*#__PURE__*/ _jsx(MiniBackButton, _objectSpread({}, props, {
        className: classNames("justify-self-start", props.className)
    }));
};
export var MODAL_ACTIONS_HEIGHT = 80;
export var ModalActions = function(props) {
    var floating = props.floating, children = props.children;
    return /*#__PURE__*/ _jsx(AnimatePresence, {
        initial: false,
        children: !floating || floating === "visible" ? /*#__PURE__*/ _jsx(motion.div, {
            className: "flex-center after:bg-tertiary-400 fixed left-0 bottom-0 right-0 z-[1] w-full gap-2 p-4 after:absolute after:top-full after:left-0 after:right-0 after:h-10",
            style: inlineEasingGradient(Base.Tertiary400, "top"),
            initial: {
                opacity: 0,
                transform: "translateY(100%)"
            },
            animate: {
                opacity: 1,
                transform: "translateY(0%)"
            },
            exit: {
                opacity: 0,
                transform: "translateY(100%)"
            },
            children: children
        }) : null
    });
};
var ModalStickyHeaderMobile = function(props) {
    var _bg = props.bg, bg = _bg === void 0 ? semanticColor("surface-overlay") : _bg, _pv = props.pv, pv = _pv === void 0 ? 16 : _pv, _ph = props.ph, ph = _ph === void 0 ? 0 : _ph, _shadow = props.shadow, shadow = _shadow === void 0 ? {
        height: 16,
        color: bg,
        to: "bottom",
        position: "bottom"
    } : _shadow, rest = _objectWithoutProperties(props, [
        "bg",
        "pv",
        "ph",
        "shadow"
    ]);
    return /*#__PURE__*/ _jsx(Sticky, {
        sticky: "top",
        shadow: shadow,
        children: /*#__PURE__*/ _jsx(ModalStickyHeaderMobileContainer, _objectSpread({}, rest, {
            bg: bg,
            ph: ph,
            pv: pv
        }))
    });
};
export { ModalStickyHeaderMobile };
var ModalStickyHeaderMobileContainer = function(props) {
    var className = props.className, bg = props.bg, _ph = props.ph, ph = _ph === void 0 ? 0 : _ph, _pv = props.pv, pv = _pv === void 0 ? 16 : _pv, _style = props.style, style = _style === void 0 ? {} : _style, rest = _objectWithoutProperties(props, [
        "className",
        "bg",
        "ph",
        "pv",
        "style"
    ]);
    return /*#__PURE__*/ _jsx("div", _objectSpread({
        className: classNames("z-stack z-[1] grid auto-rows-fr justify-items-center self-stretch sm:hidden", className)
    }, rest, {
        style: _objectSpread({}, style, {
            background: bg,
            paddingTop: pv,
            paddingBottom: pv,
            paddingLeft: ph,
            paddingRight: ph
        })
    }));
};
export { ModalStickyHeaderMobileContainer };
var ModalTwoColumnLayout = function(props) {
    var className = props.className, rest = _objectWithoutProperties(props, [
        "className"
    ]);
    return /*#__PURE__*/ _jsx("div", _objectSpread({
        className: classNames("flex flex-1 flex-col items-stretch overflow-hidden sm:flex-row", className)
    }, rest));
};
export { ModalTwoColumnLayout };
var ModalSidebarHeader = function(props) {
    var className = props.className, rest = _objectWithoutProperties(props, [
        "className"
    ]);
    return /*#__PURE__*/ _jsx("div", _objectSpread({
        className: classNames("sm:bg-secondary-400 sticky top-0 z-[1] flex flex-col items-stretch py-6 px-4 sm:mx-10 sm:px-0 sm:pt-10 sm:pb-6", className)
    }, rest));
};
export { ModalSidebarHeader };
var ModalSidebar = function(props) {
    var className = props.className, rest = _objectWithoutProperties(props, [
        "className"
    ]);
    return /*#__PURE__*/ _jsx("div", _objectSpread({
        className: classNames("bg-secondary-400 sticky top-0 flex flex-col items-stretch sm:static sm:top-auto sm:w-1/3 sm:min-w-[280px] sm:overflow-auto", className)
    }, rest));
};
export { ModalSidebar };
